import { useRef, useState } from 'react'

import styled from 'styled-components'
import ChatInput from '../../components/ChatInput'
import { useCortext } from './useCortext'
import { FormikProvider } from 'formik'
import { examples, imageExamples, OptionType, responseOptions } from './types'

import TabList from 'share-ui/components/Tabs/TabList/TabList'
import Tab from 'share-ui/components/Tabs/Tab/Tab'
import TabsContext from 'share-ui/components/Tabs/TabsContext/TabsContext'
import TabPanels from 'share-ui/components/Tabs/TabPanels/TabPanels'
import TabPanel from 'share-ui/components/Tabs/TabPanel/TabPanel'
import StreamPromptingSettings from './Settings/StreamPromptingSettings'
import ImageResponseSettings from './Settings/ImageResponseSettings'
import SettingsPortal from '../../components/SettingsPortal'
import ImagesChatWindow from '../../components/ImagesChatWindow'
import TextChatWindow from '../../components/TextChatWindow'
import { StyledSettings } from './Settings/SettingsStyles.css'
import SubnetDescription from '../../components/SubnetDescription'
import ChatHistoryList from '../../components/History/ChatHistoryList'
import ImagesHistoryList from '../../components/History/ImagesHistoryList'
import Examples from '../../components/Examples'

const Cortext = () => {
    const cortextRef = useRef(null)

    const {
        formik,
        responseLoading,
        chat,
        setResponseType,
        image,
        handleStartNewChat,
        chatHistory,
        setChat,
        handleDeleteHistory,
        scrollRef,
        displayPrompt,
        handleStopRequest,
        requestLoading,
    } = useCortext()

    const [activeTab, setActiveTab] = useState(0)
    const handleTabClick = (tabId: number, option: OptionType) => {
        setActiveTab(tabId)
        setResponseType(option)
    }

    return (
        <FormikProvider value={formik}>
            <StyledRoot ref={cortextRef}>
                <>
                    {activeTab === 0 && (
                        <ChatHistoryList
                            onNewChat={handleStartNewChat}
                            chatHistory={chatHistory}
                            handleDeleteHistory={handleDeleteHistory}
                            setChat={setChat}
                            chat={chat}
                        />
                    )}
                    {activeTab === 1 && <ImagesHistoryList />}
                </>

                <StyledBody>
                    <StyledChatHeader>
                        <div>
                            <TabList activeTabId={activeTab} noBorder={false}>
                                {responseOptions.map((option, index) => {
                                    return (
                                        <Tab
                                            key={index}
                                            onClick={() => handleTabClick(index, option)}
                                        >
                                            {option.label}
                                        </Tab>
                                    )
                                })}
                            </TabList>
                        </div>
                    </StyledChatHeader>

                    <TabsContext activeTabId={activeTab}>
                        <TabPanels noAnimation>
                            <TabPanel>
                                <TextChatWindow
                                    loading={responseLoading}
                                    messages={chat.messages}
                                    scrollRef={scrollRef}
                                />
                            </TabPanel>

                            <TabPanel>
                                <ImagesChatWindow
                                    displayPrompt={displayPrompt}
                                    loading={responseLoading}
                                    images={[image?.url || '']}
                                />
                            </TabPanel>
                        </TabPanels>
                    </TabsContext>

                    <ChatInput
                        formik={formik}
                        fieldName='message'
                        stopRequest={{ requestLoading, handleStopRequest }}
                    />
                </StyledBody>
            </StyledRoot>

            <SettingsPortal portalRef={cortextRef}>
                <StyledSettings>
                    <SubnetDescription
                        title={'18. Cortex.t'}
                        noImage={activeTab === 0}
                        noText={activeTab === 1}
                        description={'Deliver reliable, high-quality text and image responses.'}
                        docUrl={`${import.meta.env.REACT_APP_SUBNET_DOCS_URL}/cortext`}
                    />

                    {activeTab === 0 && (
                        <>
                            <Examples
                                disabled={requestLoading}
                                formik={formik}
                                chatInputName={'message'}
                                examples={examples}
                            />
                            <StreamPromptingSettings formik={formik} />
                        </>
                    )}
                    {activeTab === 1 && (
                        <>
                            <Examples
                                disabled={requestLoading}
                                formik={formik}
                                chatInputName={'message'}
                                examples={imageExamples}
                            />
                            <ImageResponseSettings formik={formik} />
                        </>
                    )}
                </StyledSettings>
            </SettingsPortal>
        </FormikProvider>
    )
}

export default Cortext

export const StyledRoot = styled.div`
    display: flex;
    width: 100%;
    height: 100%;
    gap: 10px;

    padding-top: 10px;
`

export const StyledBody = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    width: 100%;
    height: 100%;

    padding-right: 20px;
`

export const StyledChatHeader = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;

    min-height: 40px;
`
